import { Box, Stack } from "@mui/material";
import { memo } from "react";

import AudioPlayer from "@/components/AudioPlayer";
import ConfirmDeleteItem from "@/components/feed/shared/ConfirmDeleteItem";
import RichTranscript from "@/components/RichTranscript";
import SimpleModal from "@/components/SimpleModal.tsx";
import ModalForm from "@/elements/ModalForm";
import type { PreferredLanguage } from "@/models/languages";
import DebugItemId from "@/routes/workspace/debugItem.tsx";
import type { Transcription } from "@/store/types";

type Props = {
  debugMode: boolean;
  debuggerOpen: boolean;
  setDebuggerOpen: (value: boolean) => void;
  confirmDeleteDialog: boolean;
  setConfirmDeleteDialog: (value: boolean) => void;
  preventScroll: (value: boolean) => void;
  showModalTranscription: boolean;
  setShowModalTranscription: (value: boolean) => void;
  feedItemId: string;
  hasPlayableAudio: boolean;
  duration: number;
  itemId: string;
  itemFeedId: string;
  handlePlay: (time: number) => Promise<void>;
  handleFinished: () => Promise<void>;
  handlePause: (time: number) => Promise<void>;
  preferredLanguage: PreferredLanguage;
  transcriptions: Transcription[];
};

const FeedItemModals = memo(
  ({
    debugMode,
    debuggerOpen,
    setDebuggerOpen,
    confirmDeleteDialog,
    setConfirmDeleteDialog,
    preventScroll,
    showModalTranscription,
    setShowModalTranscription,
    feedItemId,
    hasPlayableAudio,
    duration,
    itemId,
    itemFeedId,
    handlePlay,
    handleFinished,
    handlePause,
    preferredLanguage,
    transcriptions,
  }: Props) => {
    return (
      <>
        {debugMode && (
          <SimpleModal
            sx={{ width: "calc(100vw - 100px)", maxWidth: "1400px" }}
            open={debuggerOpen}
            closeModal={() => setDebuggerOpen(false)}
            title="Debug Information"
          >
            <DebugItemId propItemId={itemId} propFeedId={itemFeedId} />
          </SimpleModal>
        )}

        <ConfirmDeleteItem
          open={confirmDeleteDialog}
          itemId={itemId}
          setOpen={(value) => setConfirmDeleteDialog(value)}
          setPreventScroll={(value) => preventScroll(value)}
        />

        <ModalForm
          id={`transcription-${feedItemId}`}
          open={showModalTranscription}
          onClose={() => setShowModalTranscription(false)}
          maxWidth={"80%"}
          sx={{ overflow: "hidden" }}
          keepMounted
        >
          <Stack sx={{ height: "100%" }}>
            <Box sx={{ py: 5, pt: { xs: 5, sm: 0 } }}>
              {hasPlayableAudio && (
                <AudioPlayer
                  duration={duration}
                  id={itemId}
                  containerWidth={"100%"}
                  onPlay={handlePlay}
                  onFinished={handleFinished}
                  onPause={handlePause}
                  inline
                  paperize={false}
                  disabledPlayButton={!hasPlayableAudio}
                />
              )}
            </Box>
            <Box sx={{ px: "5%", overflow: "auto" }}>
              <RichTranscript preferredLanguage={preferredLanguage} transcriptions={transcriptions} />
            </Box>
          </Stack>
        </ModalForm>
      </>
    );
  },
);

FeedItemModals.displayName = "FeedItemModals";

export default FeedItemModals;
